/**
 * Iter-106 — Maintenance · Logs (current month)
 *
 * Lists all `mission_audit_log`, `user_audit_log` and `invoices` rows for the current
 * month in a unified table. Older months are assumed to live in the shared Drive
 * folder URL configured under Maintenance · General (`logs_archive_drive_url`).
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { ExternalLink, RefreshCw, Filter, FileText, User as UserIcon, Briefcase, Archive } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { SettingsHeader } from "./_shared";

const SOURCE_META = {
  mission_audit_log: { label: "Mission", color: "bg-blue-100 text-blue-800", Icon: Briefcase },
  user_audit_log: { label: "User", color: "bg-purple-100 text-purple-800", Icon: UserIcon },
  invoices: { label: "Invoice", color: "bg-emerald-100 text-emerald-800", Icon: FileText },
  // Iter-192 — freelance invoice + CRA lifecycle events.
  freelance_invoices: { label: "Freelance", color: "bg-amber-100 text-amber-900", Icon: FileText },
  cra_submissions: { label: "CRA", color: "bg-cyan-100 text-cyan-800", Icon: FileText },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (_e) { return iso; }
}

function describe(row) {
  const d = row.details || {};
  if (row.source === "invoices") {
    return `${d.number || row.entity_id} · ${d.client_name || ""} · ${d.total_ttc ?? "?"} ${d.currency || ""}${d.period ? ` · ${d.period}` : ""}`;
  }
  if (row.source === "user_audit_log") {
    const target = d.target_name || row.entity_id || "";
    const change = [d.previous_role, d.new_role].filter(Boolean).join(" → ");
    return `${target}${change ? ` · ${change}` : ""}`;
  }
  // Iter-192 — freelance invoice rows.
  if (row.source === "freelance_invoices") {
    return `${d.user_name || "?"} · ${d.invoice_number || row.entity_id} · ${d.amount_ttc ?? "?"} ${d.currency || ""}${d.period ? ` · ${d.period}` : ""}`;
  }
  // Iter-192 — CRA submission rows.
  if (row.source === "cra_submissions") {
    return `${d.user_name || "?"}${d.period ? ` · ${d.period}` : ""}${d.status ? ` · ${d.status}` : ""}`;
  }
  // mission audit log
  const keys = Object.keys(d);
  if (keys.length === 0) return row.entity_id || "—";
  const summary = keys.slice(0, 3).map((k) => `${k}=${formatVal(d[k])}`).join(", ");
  return summary;
}

function formatVal(v) {
  if (v === null || v === undefined) return "∅";
  if (typeof v === "object") return JSON.stringify(v).slice(0, 60);
  const s = String(v);
  return s.length > 50 ? s.slice(0, 50) + "…" : s;
}

export default function LogsSection() {
  const today = new Date();
  const defaultMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const [month, setMonth] = useState(defaultMonth);
  const [data, setData] = useState({ rows: [], count: 0, archive_drive_url: "", month: defaultMonth });
  const [loading, setLoading] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [archives, setArchives] = useState([]);
  const [sourceFilter, setSourceFilter] = useState("__ALL__");
  const [actorFilter, setActorFilter] = useState("__ALL__");

  const load = async () => {
    setLoading(true);
    try {
      const [r, a] = await Promise.all([
        api.get(`/admin/logs?month=${month}`),
        api.get("/admin/logs/archives").catch(() => ({ data: { archives: [] } })),
      ]);
      setData(r.data || { rows: [] });
      setArchives(a.data?.archives || []);
    } catch (_e) { /* swallow */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const archiveNow = async () => {
    if (!data.archive_drive_url) {
      toast.error("Configure the archive Drive folder first (Maintenance · General).");
      return;
    }
    if (!window.confirm("Archive last month's PROD logs to the configured Drive folder now?")) return;
    setArchiving(true);
    try {
      const r = await api.post("/admin/logs/archive-now");
      toast.success(`Archived ${r.data.rows} rows for ${r.data.month}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Archive failed");
    } finally { setArchiving(false); }
  };

  const actorsPresent = useMemo(() => {
    const set = new Set();
    (data.rows || []).forEach((r) => {
      if (r.actor_name) set.add(`${r.actor_id}|${r.actor_name}`);
    });
    return Array.from(set).map((s) => {
      const [id, name] = s.split("|");
      return { id, name };
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [data.rows]);

  const filtered = useMemo(() => {
    return (data.rows || []).filter((r) => {
      if (sourceFilter !== "__ALL__" && r.source !== sourceFilter) return false;
      if (actorFilter !== "__ALL__" && r.actor_id !== actorFilter) return false;
      return true;
    });
  }, [data.rows, sourceFilter, actorFilter]);

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1200px]" data-testid="settings-page-maintenance-logs">
      <SettingsHeader
        kicker="Maintenance · Logs"
        title="Activity logs (current month)"
        description="Mission edits, user role changes and invoices generated this month. Older months are archived in the Drive folder configured under Maintenance · General."
      />

      <div className="bg-white border border-zinc-200 rounded-lg p-5 space-y-4 mb-6">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-44">
            <label className="text-[10px] uppercase font-mono text-zinc-500 mb-1 block">Month</label>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              data-testid="logs-month-picker"
              className="w-full h-9 px-3 border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-navy/30"
            />
          </div>
          <div className="w-44">
            <label className="text-[10px] uppercase font-mono text-zinc-500 mb-1 block">Source</label>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger data-testid="logs-source-filter" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__ALL__">All sources</SelectItem>
                <SelectItem value="mission_audit_log">Mission edits</SelectItem>
                <SelectItem value="user_audit_log">User changes</SelectItem>
                <SelectItem value="invoices">Invoices</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-56">
            <label className="text-[10px] uppercase font-mono text-zinc-500 mb-1 block">Actor</label>
            <Select value={actorFilter} onValueChange={setActorFilter}>
              <SelectTrigger data-testid="logs-actor-filter" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__ALL__">All actors</SelectItem>
                {actorsPresent.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <button
            onClick={load}
            disabled={loading}
            data-testid="logs-refresh"
            className="inline-flex items-center gap-1.5 px-3 h-9 border border-zinc-200 text-xs rounded-md hover:bg-zinc-50 disabled:opacity-50"
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            onClick={archiveNow}
            disabled={archiving || !data.archive_drive_url}
            data-testid="logs-archive-now"
            title={data.archive_drive_url ? "Archive last month's PROD logs to Drive now" : "Configure Drive folder URL in Maintenance · General first"}
            className="inline-flex items-center gap-1.5 px-3 h-9 bg-zinc-950 text-white text-xs rounded-md hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Archive size={13} /> {archiving ? "Archiving…" : "Archive last month"}
          </button>
          <div className="ml-auto text-xs text-zinc-500 font-mono">
            {filtered.length} / {data.count} rows
          </div>
        </div>

        {data.archive_drive_url ? (
          <div className="text-[11px] text-zinc-600 bg-zinc-50 border border-zinc-200 rounded p-3 leading-relaxed">
            <Filter size={11} className="inline mr-1" />
            Looking for an older month? <a href={data.archive_drive_url} target="_blank" rel="noreferrer" data-testid="logs-archive-link" className="text-blue-700 hover:underline inline-flex items-center gap-1 font-medium">Open the archive Drive folder <ExternalLink size={11} /></a>
          </div>
        ) : (
          <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-3 leading-relaxed">
            <strong>Tip :</strong> set the archive Drive folder URL under <em>Maintenance · General</em> so older months are linkable from this page.
          </div>
        )}
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg md:overflow-x-auto">
        <div className="hidden md:grid md:grid-cols-12 px-4 py-2.5 text-[10px] uppercase tracking-widest text-zinc-500 font-mono bg-zinc-50 border-b border-zinc-200 md:min-w-[900px]">
          <div className="col-span-2">Date</div>
          <div className="col-span-2">Source</div>
          <div className="col-span-2">Actor</div>
          <div className="col-span-2">Action</div>
          <div className="col-span-4">Details</div>
        </div>
        {filtered.length === 0 && (
          <div className="px-5 py-12 text-center text-sm text-zinc-500" data-testid="logs-empty">
            No log entry {data.count === 0 ? "for this month" : "matches the active filters"}.
          </div>
        )}
        {filtered.map((r, idx) => {
          const meta = SOURCE_META[r.source] || { label: r.source, color: "bg-zinc-100 text-zinc-700", Icon: FileText };
          const Icon = meta.Icon;
          return (
            <div
              key={`${r.source}-${r.entity_id}-${idx}`}
              data-testid={`log-row-${idx}`}
              className="flex flex-col gap-2 md:grid md:grid-cols-12 md:items-center md:gap-0 px-4 py-3 text-xs border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 md:min-w-[900px]"
            >
              <div className="col-span-2 font-mono text-zinc-700">{fmtDate(r.timestamp)}</div>
              <div className="col-span-2">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono ${meta.color}`}>
                  <Icon size={10} /> {meta.label}
                </span>
              </div>
              <div className="col-span-2 truncate" title={r.actor_name}>{r.actor_name || "—"}</div>
              <div className="col-span-2 font-mono text-[11px] text-zinc-700 truncate" title={r.action}>{r.action}</div>
              <div className="col-span-4 text-zinc-700 truncate" title={describe(r)}>{describe(r)}</div>
            </div>
          );
        })}
      </div>

      {/* Past monthly archives — auto-pushed by the cron on day 1 of each month */}
      {archives.length > 0 && (
        <div className="mt-8" data-testid="logs-past-archives">
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-2 flex items-center gap-2">
            <Archive size={12} /> Past archives
          </div>
          <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
            {archives.map((a) => (
              <div key={`${a.month}-${a.drive_file_id}`} className="flex items-center justify-between gap-3 px-4 py-3 border-b border-zinc-100 last:border-b-0 text-xs">
                <div className="font-mono text-zinc-800">{a.month}</div>
                <div className="text-zinc-500">{a.rows} rows</div>
                <div className="font-mono text-zinc-500">{fmtDate(a.uploaded_at)}</div>
                {a.drive_url && (
                  <a
                    href={a.drive_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`past-archive-${a.month}`}
                    className="ml-auto inline-flex items-center gap-1 text-blue-700 hover:underline"
                  >
                    Open <ExternalLink size={11} />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
